import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  Stack,
  Typography,
  SelectChangeEvent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  IconButton,
  Popover,
  CircularProgress,
  Alert,
  Tooltip,
  InputAdornment,
  Theme,
  Divider,
  FormControlLabel,
  Checkbox,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  Avatar,
  Drawer,
  Grid,
  TableBody,
} from '@mui/material';
import { PageWrapper } from '../../components';
import { useAppSelector, useAppDispatch } from '../../redux/hooks';
import { PageTitle } from '../../utils/pageTitles';
import { useTitle } from '../../hooks/useTitle';
import { sowAdminSelectors } from '../../redux/sow-admin/sow-admin.selectors';
import {
  cleanDate,
  cleanPortfolioDate,
  executeSowChangeReq,
  fetchSowAdminAccounts,
  fetchSowChangeReqs,
  rejectSowChangeReq,
  requestBulkDeactivate,
  requestDeactivate,
  requestEdit,
  requestMove,
  resetRequestStates,
  setPortfolioDate,
  setPortfolioGlobalDate,
  resetSowAdminFilters,
  setDate,
  setGlobalDate,
  setSowAdminFilters,
  SoWFilters,
} from '../../redux/sow-admin';
import { DataState } from '../../types/asyncData';
import {
  AccountAdminDto,
  ProjectAdminDto,
  PutResourceDto,
  ResourceStatus,
  SowStatus,
  UserForAdminDto,
  AccountStatus,
} from '../../types';
import {
  AutoDeleteOutlined as DeletionPendingIcon,
  DeleteOutlined,
  FilterList as FilterListIcon,
  InfoOutlined,
  EmailOutlined,
  PeopleOutlineOutlined,
  Event as CalendarIcon,
} from '@mui/icons-material';
import ContactsRoundedIcon from '@mui/icons-material/ContactsRounded';
import { userDataSelectors } from '../../redux/userData';
import { useFormik } from 'formik';
import { COLORS } from '../../utils/colors';
import CircleIcon from '@mui/icons-material/Circle';
import { MemberInfoCell, priceFormat } from '../../components/tableCellRenders';
import { DateTime } from 'luxon';
import { getBillingType } from '../../utils/business';
import { calculateAccountSowStatus } from '../../redux/accounts';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { DatePicker } from '@mui/x-date-pickers';
import { formatDateTo_yyyy_MM_dd, formatStringToDate } from '../../utils/date';
import {
  DeactivateResourceDto,
  DeactivateMultipleResourceDto,
  DeactivationReason,
  DEACTIVATION_REASONS,
  Seniority,
  seniorityValues,
  sowStreamValues,
  sowStreamsLabels,
  SowStream,
  getSowSeniorities,
  getSowStreamFromLabel,
} from '../../types/accounts';
import * as _ from 'lodash';
import EditIcon from '@mui/icons-material/Edit';
import { parseFormikErrors } from '../../utils/error';
import {
  BusinessAction,
  SowChangeReqForApprovalDto,
  SowChangeReqForApprovalRespDto,
} from '../../types/sow-change-req';
import CustomDatePicker from '../../components/datePicker/CustomDatePicker';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { ListItemCard } from '../../components/listItemCard/ListItemCard';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import WorkOffOutlinedIcon from '@mui/icons-material/WorkOffOutlined';

const getProjectSowStatus = ({ members }: ProjectAdminDto): SowStatus => {
  if (members.length === 0) {
    return SowStatus.Potential;
  }
  const hasActiveMember = members.some(
    (m) => m.status === ResourceStatus.ACTIVE,
  );
  if (hasActiveMember) {
    return SowStatus.Active;
  }
  const areAllMembersInactive = members.every(
    (m) => m.status === ResourceStatus.INACTIVE,
  );
  if (areAllMembersInactive) {
    return SowStatus.Expired;
  }
  return SowStatus.Potential;
};

interface SowAdminProps {
  accountIdFilter?: string;
  projectIdFilter?: string;
  embedded?: boolean;
}

export const SowAdmin = ({
  accountIdFilter,
  projectIdFilter,
  embedded,
}: SowAdminProps) => {
  const setPageTitle = useTitle();
  const [showClosedAccounts, setShowClosedAccounts] = useState(false);

  const dispatch = useAppDispatch();
  const isSingleAccountView = !!accountIdFilter;

  const state = useAppSelector(
    isSingleAccountView
      ? sowAdminSelectors.getPortfolioAccountsState
      : sowAdminSelectors.getAccountsState,
  );
  const sowAccounts = useAppSelector(
    isSingleAccountView
      ? sowAdminSelectors.getPortfolioAccounts
      : sowAdminSelectors.getAccounts,
  );

  useEffect(() => {
    if (!embedded) {
      setPageTitle(PageTitle.sowManagement);
    } else {
      const accountName = sowAccounts?.[0]?.name ?? PageTitle.portfolio;
      setPageTitle(`${accountName} | ${PageTitle.embeddedSow}`);
    }
  }, [embedded, setPageTitle, sowAccounts]);

  const projects = useAppSelector(
    isSingleAccountView
      ? sowAdminSelectors.getPortfolioProjects
      : sowAdminSelectors.getProjects,
  );

  const date = useAppSelector(
    isSingleAccountView
      ? sowAdminSelectors.getPortfolioDate
      : sowAdminSelectors.getDate,
  );

  const [editResourceId, setEditResourceId] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<'Active' | 'Expired'>(
    'Active',
  );
  const sowChangeReqs = useAppSelector(sowAdminSelectors.getSowChangeReqs);

  const filters = useAppSelector(sowAdminSelectors.getSowAdminFilters);
  const uniqueFilterLists = useAppSelector(
    sowAdminSelectors.getUniqueFilterLists,
  );

  const user = useAppSelector(userDataSelectors.getUserData);
  const isFinance = useMemo(() => user?.permissions.isFinance ?? false, [user]);

  useEffect(() => {
    setShowClosedAccounts(false);
    dispatch(
      fetchSowAdminAccounts({
        date,
        isPortfolio: isSingleAccountView,
        accountId: accountIdFilter,
        pdmId:
          filters.PDMsToFilter.length > 0 ? filters.PDMsToFilter : undefined,
        dmId: filters.DMsToFilter.length > 0 ? filters.DMsToFilter : undefined,
      }),
    );
  }, [dispatch, date, filters, isSingleAccountView, accountIdFilter]);

  useEffect(() => {
    setShowClosedAccounts(false);
  }, [statusFilter]);

  const accounts = useMemo(() => {
    let filteredAccounts = sowAccounts;
    if (!filteredAccounts) {
      return filteredAccounts;
    }

    filteredAccounts = filteredAccounts
      .map((account) => {
        const filteredProjects = account.projects.filter((p) => {
          if (projectIdFilter && p.id !== projectIdFilter) {
            return false;
          }

          const projectStatus = getProjectSowStatus(p);
          if (statusFilter === 'Active') {
            return (
              projectStatus === SowStatus.Active ||
              projectStatus === SowStatus.Potential
            );
          } else {
            return projectStatus === SowStatus.Expired;
          }
        });

        return {
          ...account,
          projects: filteredProjects,
        };
      })
      .filter((account) => account.projects.length > 0);

    return filteredAccounts;
  }, [sowAccounts, statusFilter, projectIdFilter]);

  const activeAccounts = useMemo(() => {
    if (!accounts) return null;
    return accounts.filter((acc) => acc.status !== AccountStatus.Closed);
  }, [accounts]);

  const closedAccounts = useMemo(() => {
    if (!accounts) return null;
    return accounts.filter((acc) => acc.status === AccountStatus.Closed);
  }, [accounts]);

  useEffect(() => {
    dispatch(fetchSowChangeReqs({}));
  }, [dispatch]);

  const onSetEditResourceId = useCallback(
    (resourceId: string | null) => {
      setEditResourceId(resourceId);
    },
    [setEditResourceId],
  );

  const onDeactivateResource = useCallback(
    (data: DeactivateResourceDto) => {
      dispatch(requestDeactivate(data));
    },
    [dispatch],
  );

  const { editResource, relatedProjects, relatedProject, relatedAccount } =
    useMemo(() => {
      if (projects === null || accounts === null)
        return {
          editResource: null,
          relatedProjects: null,
          relatedProject: null,
        };

      const relatedProject = projects.find((project) =>
        project.members.some((member) => member.resourceId === editResourceId),
      );

      if (!relatedProject)
        return {
          editResource: null,
          relatedProjects: null,
          relatedProject: null,
          relatedAccount: null,
        };

      const relatedAccount = accounts.find((account) =>
        account.projects.find((project) => project.id === relatedProject.id),
      );

      if (!relatedAccount)
        return {
          editResource: null,
          relatedProjects: null,
          relatedProject: null,
          relatedAccount: null,
        };

      const relatedProjects = relatedAccount.projects;

      const editResource = relatedProject.members.find(
        (member) => member.resourceId === editResourceId,
      );

      if (!editResource)
        return {
          editResource: null,
          relatedProjects: null,
          relatedProject: null,
          relatedAccount: null,
        };

      return { editResource, relatedProjects, relatedProject, relatedAccount };
    }, [accounts, projects, editResourceId]);

  return (
    <PageWrapper
      isLoading={state === DataState.Pending}
      sx={{
        p: embedded ? 0 : 2,
        pt: embedded ? 0 : 2,
      }}
    >
      <SowTableHeader
        isFinance={isFinance}
        sowChangeReqs={sowChangeReqs}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        isSingleAccountView={isSingleAccountView}
        date={date}
        setDate={setDate}
        filters={filters}
        uniqueFilterLists={uniqueFilterLists}
      />
      {activeAccounts && (
        <SowAccountsTable
          accounts={activeAccounts}
          onSetEditResourceId={onSetEditResourceId}
          onDeactivateResource={onDeactivateResource}
          isSingleAccountView={!!accountIdFilter}
          projectIdFilter={projectIdFilter}
        />
      )}

      {Array.isArray(activeAccounts) && activeAccounts.length === 0 && (
        <Box
          display="flex"
          justifyContent="center"
          alignContent="center"
          sx={{
            height: '10vh',
            backgroundColor: 'white',
            display: 'grid',
            verticalAlign: 'middle',
          }}
        >
          <Typography variant="h3" mx="2" color="darkslategray">
            {statusFilter === 'Active'
              ? 'No active SOW found'
              : 'No expired SOW found'}
          </Typography>
        </Box>
      )}

      {!embedded && accounts && (
        <Stack
          sx={{
            padding: '16px 0px 16px 16px',
            bgcolor: 'background.default',
            width: '100%',
          }}
        >
          <Stack flexDirection="row" gap="8px">
            <WorkOffOutlinedIcon color="action" />
            <Button
              variant="text"
              sx={{
                textDecoration: 'underline',
                cursor: 'pointer',
                color: 'black',
                fontSize: '14px',
                padding: '0',
                background: 'none !important',
              }}
              onClick={() => setShowClosedAccounts((prev) => !prev)}
            >
              {showClosedAccounts
                ? `Hide closed accounts with ${statusFilter.toLocaleLowerCase()} SOWs`
                : `Show closed accounts with ${statusFilter.toLocaleLowerCase()} SOWs`}
            </Button>
          </Stack>
        </Stack>
      )}

      {closedAccounts && closedAccounts.length > 0 && showClosedAccounts && (
        <Box sx={{ mt: '10px' }}>
          {closedAccounts.map((account) => (
            <AccountRow
              key={account.id}
              account={account}
              onSetEditResourceId={onSetEditResourceId}
              onDeactivateResource={onDeactivateResource}
            />
          ))}
        </Box>
      )}

      {closedAccounts?.length === 0 && showClosedAccounts && (
        <TableBody
          sx={{
            display: 'flex',
            alignContent: 'center',
            justifyContent: 'center',
            marginTop: '2%',
            marginBottom: '2%',
            fontSize: '1rem',
            color: 'grey',
          }}
        >
          {'No closed accounts found'}
        </TableBody>
      )}
      {editResourceId &&
        editResource &&
        relatedProjects &&
        relatedProject &&
        relatedAccount && (
          <SowResourceEditModal
            resource={editResource}
            setEditResourceId={setEditResourceId}
            relatedProjects={relatedProjects}
            relatedProject={relatedProject}
            relatedAccount={relatedAccount}
          />
        )}
    </PageWrapper>
  );
};

interface SowTableHeaderProps {
  isFinance: boolean;
  sowChangeReqs: SowChangeReqForApprovalRespDto | null;
  statusFilter: 'Active' | 'Expired';
  setStatusFilter: (val: 'Active' | 'Expired') => void;
  isSingleAccountView?: boolean;
  date: string | null;
  setDate: (date: string | null) => void;
  filters: SoWFilters;
  uniqueFilterLists: {
    pdms: { value: string; label: string }[];
    dms: { value: string; label: string }[];
  };
}

export const SowTableHeader = ({
  isFinance,
  sowChangeReqs,
  statusFilter,
  setStatusFilter,
  isSingleAccountView,
  filters,
  uniqueFilterLists,
}: SowTableHeaderProps) => {
  const [isSowChangeReqsOpen, setIsSowChangeReqsOpen] = useState(false);
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const sowChangeReqsPopoverAnchor = React.useRef<HTMLButtonElement | null>(
    null,
  );
  const dateLocal = useAppSelector(
    isSingleAccountView
      ? sowAdminSelectors.getPortfolioDateLocal
      : sowAdminSelectors.getDateLocal,
  );

  const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (isSingleAccountView) {
      setPortalTarget(
        document.getElementById('sow-header-controls-portal-target'),
      );
    }
  }, [isSingleAccountView]);
  const dispatch = useAppDispatch();
  const isFiltersActive =
    filters.PDMsToFilter.length > 0 || filters.DMsToFilter.length > 0;

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      setIsSowChangeReqsOpen(!isSowChangeReqsOpen);
    },
    [setIsSowChangeReqsOpen, isSowChangeReqsOpen],
  );

  const onChangeFilterValue = useCallback(
    (event: SelectChangeEvent<string[]>) => {
      const { name, value } = event.target;

      dispatch(
        setSowAdminFilters({
          ...filters,
          [name]: typeof value === 'string' ? value.split(',') : value,
        }),
      );
    },
    [dispatch, filters],
  );

  const onDeleteFilterValue = useCallback(
    (value: string, name: 'PDMsToFilter' | 'DMsToFilter') => {
      dispatch(
        setSowAdminFilters({
          ...filters,
          [name]: filters[name].filter((x) => x !== value),
        }),
      );
    },
    [dispatch, filters],
  );

  const resetFilters = useCallback(() => {
    dispatch(resetSowAdminFilters());
  }, [dispatch]);

  const headerControls = (
    <Box display="flex" alignItems="center" ml="auto" flexWrap="wrap" gap={1}>
      <ToggleButtonGroup
        aria-label="Status"
        value={statusFilter}
        exclusive
        onChange={(e, val) => {
          if (val !== null) setStatusFilter(val);
        }}
        size="small"
        sx={{
          mr: 2,
          '& .MuiToggleButton-root': {
            border: '1px solid #00000026',
            px: 2,
            py: 0.5,
            backgroundColor: COLORS.WHITE,
            textTransform: 'none',
            color: COLORS.TEXT.REGULAR,
            fontSize: '14px',
            fontWeight: 400,

            '&.Mui-selected': {
              backgroundColor: 'rgba(0, 0, 0, 0.08)',
              color: COLORS.TEXT.REGULAR,

              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.12)',
              },
            },

            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
          },
        }}
      >
        <ToggleButton
          value="Active"
          sx={{ fontSize: '12px', textTransform: 'none' }}
        >
          Active
        </ToggleButton>
        <ToggleButton
          value="Expired"
          sx={{ fontSize: '12px', textTransform: 'none' }}
        >
          Expired
        </ToggleButton>
      </ToggleButtonGroup>

      {!isSingleAccountView && (
        <>
          <Button
            sx={{
              mr: '8px',
              ...(!isFiltersActive && { color: 'text.primary' }),
            }}
            variant={isFiltersActive ? 'contained' : 'text'}
            onClick={() => setIsFiltersOpen(true)}
            size="small"
            {...(isFiltersActive && { color: 'secondary' })}
          >
            <FilterListIcon />
            &nbsp; Filters
            <ChevronRightIcon />
          </Button>
          <Button
            variant="text"
            onClick={resetFilters}
            size="small"
            sx={{
              mr: '8px',
              backgroundColor: '#9c27b020',
              ...(!isFiltersActive && {
                display: 'none',
                visibility: 'hidden',
              }),
            }}
            {...(isFiltersActive && { color: 'secondary' })}
          >
            <RemoveCircleOutlineIcon />
            &nbsp; Reset
          </Button>
        </>
      )}

      <CustomDatePicker
        dateFilterValueLocal={dateLocal}
        setDateFilter={{
          type: 'dispatch',
          func: isSingleAccountView ? setPortfolioDate : setDate,
          setGlobalFunc: isSingleAccountView
            ? setPortfolioGlobalDate
            : setGlobalDate,
          clean: isSingleAccountView ? cleanPortfolioDate : cleanDate,
        }}
        buttonSx={{ mr: '8px' }}
      />
      {!!isFinance && (
        <>
          <Button
            variant="contained"
            sx={{
              color: 'white',
              padding: '4px 8px',
              backgroundColor: '#9b2aaf',
              fontSize: '13px',
              '&:hover': {
                backgroundColor: '#9b2aaf',
              },
            }}
            onClick={handleClick}
            ref={sowChangeReqsPopoverAnchor}
          >
            Change requests: {sowChangeReqs?.sowChangeReqs.length}{' '}
            {isSowChangeReqsOpen ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
          </Button>
        </>
      )}
      <SowChReqPopover
        sowChangeReqsPopoverAnchor={sowChangeReqsPopoverAnchor}
        sowChangeReqs={sowChangeReqs?.sowChangeReqs ?? []}
        sowSpreadsheetLink={sowChangeReqs?.sowSpreadsheetLink ?? ''}
        isOpen={isSowChangeReqsOpen}
      />
      {!isSingleAccountView && (
        <SowAdminFiltersDrawer
          filters={filters}
          isActive={isFiltersActive}
          isOpen={isFiltersOpen}
          onChangeFilterValue={onChangeFilterValue}
          onClose={() => setIsFiltersOpen(false)}
          onDeleteFilterValue={onDeleteFilterValue}
          resetFilters={resetFilters}
          uniqueFilterLists={uniqueFilterLists}
        />
      )}
    </Box>
  );

  if (isSingleAccountView) {
    return portalTarget ? createPortal(headerControls, portalTarget) : null;
  }

  return (
    <Stack>
      <Box
        sx={(theme) => ({
          display: 'flex',
          height: theme.spacing(8),
          padding: theme.spacing(1, 1),
          borderRadius: theme.spacing(1, 1, 0, 0),
          alignItems: 'center',
        })}
      >
        <Box>
          {!isSingleAccountView && (
            <Typography variant="h2">SOW management</Typography>
          )}
        </Box>
        {headerControls}
      </Box>
    </Stack>
  );
};

const ALL_AT_ONCE = 'All at once';

interface SowAdminFiltersDrawerProps {
  filters: SoWFilters;
  isActive: boolean;
  isOpen: boolean;
  onChangeFilterValue: (event: SelectChangeEvent<any>) => void;
  onClose: () => void;
  onDeleteFilterValue: (
    value: string,
    name: 'PDMsToFilter' | 'DMsToFilter',
  ) => void;
  resetFilters: () => void;
  uniqueFilterLists: {
    pdms: { value: string; label: string }[];
    dms: { value: string; label: string }[];
  };
}

const SowAdminFiltersDrawer = ({
  filters,
  isActive,
  isOpen,
  onChangeFilterValue,
  onClose,
  onDeleteFilterValue,
  resetFilters,
  uniqueFilterLists,
}: SowAdminFiltersDrawerProps) => {
  return (
    <Drawer
      anchor="right"
      open={isOpen}
      keepMounted
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          minWidth: 388,
          maxWidth: 400,
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
        Filters
        {isActive && (
          <Button variant="text" onClick={resetFilters}>
            <RemoveCircleOutlineIcon
              sx={{ color: (theme) => theme.palette.primary.main }}
            />
            &nbsp;Reset all
          </Button>
        )}
      </DialogTitle>
      <IconButton
        aria-label="close"
        sx={{ position: 'absolute', right: 8, top: 8 }}
        onClick={onClose}
      >
        <CloseIcon />
      </IconButton>
      <Grid container padding={2} spacing={2}>
        <Grid item xs={12}>
          <FormControl fullWidth>
            <InputLabel id="sow-pdm-filter-label">PDM</InputLabel>
            <Select
              labelId="sow-pdm-filter-label"
              multiple
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip
                      key={value}
                      onDelete={() =>
                        onDeleteFilterValue(value, 'PDMsToFilter')
                      }
                      onMouseDown={(event) => event.stopPropagation()}
                      label={
                        uniqueFilterLists.pdms.find((x) => x.value === value)
                          ?.label || 'Name not found'
                      }
                    />
                  ))}
                </Box>
              )}
              value={filters.PDMsToFilter}
              label="PDM"
              onChange={onChangeFilterValue}
              name="PDMsToFilter"
            >
              {uniqueFilterLists.pdms.map((item) => (
                <MenuItem key={item.value} value={item.value}>
                  {item.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <FormControl fullWidth>
            <InputLabel id="sow-dm-filter-label">DM</InputLabel>
            <Select
              labelId="sow-dm-filter-label"
              multiple
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip
                      key={value}
                      onDelete={() => onDeleteFilterValue(value, 'DMsToFilter')}
                      onMouseDown={(event) => event.stopPropagation()}
                      label={
                        uniqueFilterLists.dms.find((x) => x.value === value)
                          ?.label || 'Name not found'
                      }
                    />
                  ))}
                </Box>
              )}
              value={filters.DMsToFilter}
              label="DM"
              onChange={onChangeFilterValue}
              name="DMsToFilter"
            >
              {uniqueFilterLists.dms.map((item) => (
                <MenuItem key={item.value} value={item.value}>
                  {item.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>
    </Drawer>
  );
};

interface SowAccountsTableProps {
  accounts: AccountAdminDto[];
  onSetEditResourceId: (resourceId: string) => void;
  onDeactivateResource: (body: DeactivateResourceDto) => void;
  isSingleAccountView?: boolean;
  projectIdFilter?: string;
}

const SowAccountsTable = ({
  accounts,
  onSetEditResourceId,
  onDeactivateResource,
  isSingleAccountView,
  projectIdFilter,
}: SowAccountsTableProps) => {
  const sortedAccounts = _.sortBy(accounts, (acc) => acc.name);

  return (
    <Box>
      {/* Sticky header */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 100,
          backgroundColor: 'white',
          borderBottom: '1px solid #E0E0E0',
          display: 'grid',
          gridTemplateColumns: '10px 4fr 1fr 1.5fr 1.5fr 1.5fr 1.8fr 1.5fr',
          fontSize: '13px',
          color: COLORS.GRAY.SLATE,
          fontWeight: 600,
          py: 1,
          pl: 3, // Increased left padding to align with content
          pr: 1,
        }}
      >
        <Box></Box>
        <Box sx={{ textAlign: 'left', pl: 6 }}>Account / Project</Box>
        <Box sx={{ textAlign: 'center', pl: 5 }}>Zoho</Box>
        <Box sx={{ textAlign: 'right', px: 1 }}>Past revenue</Box>
        <Box sx={{ textAlign: 'left', pl: 3 }}>Last invoice</Box>
        <Box sx={{ textAlign: 'left', px: 1 }}>Billing type</Box>
        <Box sx={{ textAlign: 'left', px: 1 }}>Revenue end date</Box>
        <Box sx={{ textAlign: 'left', px: 1 }}>Status</Box>
      </Box>

      {/* Rows */}
      <Box>
        {isSingleAccountView
          ? sortedAccounts.flatMap((account) =>
              account.projects.map((project) => (
                <ProjectRow
                  key={project.id}
                  project={project}
                  onSetEditResourceId={onSetEditResourceId}
                  onDeactivateResource={onDeactivateResource}
                  isSingleAccountView={isSingleAccountView}
                  expanded={!!projectIdFilter}
                />
              )),
            )
          : sortedAccounts.map((account) => (
              <AccountRow
                key={account.id}
                account={account}
                onSetEditResourceId={onSetEditResourceId}
                onDeactivateResource={onDeactivateResource}
              />
            ))}
      </Box>
    </Box>
  );
};

interface AccountRowProps {
  account: AccountAdminDto;
  onSetEditResourceId: (resourceId: string) => void;
  onDeactivateResource: (body: DeactivateResourceDto) => void;
}

const AccountRow = ({
  account,
  onSetEditResourceId,
  onDeactivateResource,
}: AccountRowProps) => {
  const accountSows = useMemo(() => {
    return calculateAccountSowStatus(account);
  }, [account]);

  const accountTitle = (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      {account.name}
      <ContactsRoundedIcon
        sx={{ fontSize: 14, color: COLORS.GRAY.SLATE, mr: 1, ml: 2 }}
      />
      <Typography whiteSpace="nowrap" variant="body2">
        {account.projects.length}
      </Typography>
      {!!account.pendingRequestsCount && (
        <Tooltip
          title={`${account.pendingRequestsCount} pending ${
            account.pendingRequestsCount > 1 ? 'changes' : 'change'
          }`}
        >
          <Box display={'flex'} alignItems={'center'}>
            <HistoryOutlinedIcon
              sx={{ fontSize: 16, color: COLORS.BRAND.PRIMARY, ml: 2, mr: 0.5 }}
            />
            <Typography sx={{ color: COLORS.BRAND.PRIMARY }}>
              {account.pendingRequestsCount}
            </Typography>
          </Box>
        </Tooltip>
      )}
    </Box>
  );

  return (
    <Box
      sx={{
        fontSize: '14px',
        background: '#fff',
        borderBottom: '1px solid #E0E0E0',
        mb: 1,
        p: 0,
      }}
    >
      <ListItemCard
        title=""
        // expanded={isOpen}
        isClickOpenCard={true}
        styles={{
          marginBottom: 0,
          overflow: 'initial',
        }}
        titleWrapper={() => (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '10px 4fr 1fr 1.5fr 1.5fr 1.5fr 1.8fr 1.5fr',
              width: '100%',
              alignItems: 'center',
              minWidth: 0,
            }}
          >
            <Box></Box>
            <Box
              sx={{
                pl: 0, // Reduced left padding to move text left
                pr: 1,
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {accountTitle}
            </Box>
            <Box sx={{ pl: 0, pr: 1, textAlign: 'center' }}>
              {Boolean(account.departmentName) ? (
                <CircleIcon sx={{ color: '#4CAF50', fontSize: 12 }} />
              ) : (
                <Tooltip title="Account name not found in Zoho People">
                  <CircleIcon sx={{ color: '#EF5350', fontSize: 12 }} />
                </Tooltip>
              )}
            </Box>
            <Box sx={{ px: 1, textAlign: 'right' }}>
              {account.pastRevenue ? (
                priceFormat({
                  value: Math.round(account.pastRevenue),
                })
              ) : (
                <Typography sx={{ pr: 3 }}>{'—'}</Typography>
              )}
            </Box>
            <Box
              sx={{
                px: 1,
                color: COLORS.GRAY.SLATE,
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {account.revenueLastInvoiceDate
                ? DateTime.fromFormat(
                    account.revenueLastInvoiceDate,
                    'yyyy-LL-dd',
                  ).toFormat('LL/dd/yyyy')
                : '—'}
            </Box>
            <Box sx={{ px: 1, color: COLORS.GRAY.SLATE }}>—</Box>
            <Box
              sx={{
                px: 1,
                color: COLORS.GRAY.SLATE,
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              —
            </Box>
            <Box sx={{ px: 1, color: COLORS.GRAY.SLATE, minWidth: 0 }}>
              <Typography
                whiteSpace="nowrap"
                variant="body1"
                sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
              >
                {accountSows.potential === account.projects.length
                  ? SowStatus.Potential
                  : accountSows.expired
                  ? SowStatus.Expired
                  : SowStatus.Active}
              </Typography>
              <Typography
                whiteSpace="nowrap"
                variant="body2"
                sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
              >
                {!!accountSows.potential &&
                  `${accountSows.potential} ${SowStatus.Potential} projects`}
              </Typography>
            </Box>
          </Box>
        )}
      >
        {account.projects.map((project) => (
          <ProjectRow
            key={project.id}
            project={project}
            onSetEditResourceId={onSetEditResourceId}
            onDeactivateResource={onDeactivateResource}
          />
        ))}
      </ListItemCard>
    </Box>
  );
};

interface ProjectRowProps {
  project: ProjectAdminDto;
  onSetEditResourceId: (resourceId: string) => void;
  onDeactivateResource: (body: DeactivateResourceDto) => void;
  isSingleAccountView?: boolean;
  expanded?: boolean;
}

const ProjectRow = ({
  project,
  onSetEditResourceId,
  onDeactivateResource,
  isSingleAccountView,
  expanded,
}: ProjectRowProps) => {
  const projectTitle = (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      {project.name}
      <PeopleAltOutlinedIcon
        sx={{ fontSize: 14, color: COLORS.GRAY.SLATE, mr: 1, ml: 2 }}
      />
      <Typography whiteSpace="nowrap" variant="body2">
        {project.members.length}
      </Typography>
      {!!project.pendingRequestsCount && (
        <Tooltip
          title={`${project.pendingRequestsCount} pending ${
            project.pendingRequestsCount > 1 ? 'changes' : 'change'
          }`}
        >
          <Box display={'flex'} alignItems={'center'}>
            <HistoryOutlinedIcon
              sx={{ fontSize: 16, color: COLORS.BRAND.PRIMARY, ml: 2, mr: 0.5 }}
            />
            <Typography sx={{ color: COLORS.BRAND.PRIMARY }}>
              {project.pendingRequestsCount}
            </Typography>
          </Box>
        </Tooltip>
      )}
    </Box>
  );

  return (
    <Box
      sx={{
        background: (theme) => theme.palette.highlight.neutral,
        fontSize: '14px',
        borderBottom: '1px solid #E0E0E0',
        '&:last-child': {
          borderBottom: 'none',
        },
        ml: isSingleAccountView ? 0 : 2,
        mr: isSingleAccountView ? 0 : 1,
        mt: isSingleAccountView ? 1 : 2,
        pb: 0,
        borderRadius: '4px',
      }}
    >
      <ListItemCard
        expanded={expanded}
        title=""
        isClickOpenCard={true}
        styles={{
          marginBottom: 0,
          marginTop: 1,
          overflow: 'initial',
          background: (theme: any) => theme.palette.highlight.neutral,
        }}
        titleWrapper={() => (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '10px 4fr 1fr 1.5fr 1.5fr 1.5fr 1.8fr 1.5fr',
              width: '100%',
              alignItems: 'center',
              minWidth: 0,
            }}
          >
            <Box></Box>
            <Box
              sx={{
                pl: 0, // Reduced left padding to move text left
                pr: 1,
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {projectTitle}
            </Box>
            <Box sx={{ pl: 0, pr: 3, textAlign: 'center' }}>
              {Boolean(project.isFoundInZp) ? (
                <CircleIcon sx={{ color: '#4CAF50', fontSize: 12 }} />
              ) : (
                <Tooltip title="Project name not found in Zoho People">
                  <CircleIcon sx={{ color: '#EF5350', fontSize: 12 }} />
                </Tooltip>
              )}
            </Box>
            <Box
              sx={{
                px: 1,
                ...(!project.projectedRevenueEndDate && {
                  color: COLORS.GRAY.SLATE,
                }),
                textAlign: 'right',
                pr: 2,
              }}
            >
              {project.pastRevenue ? (
                priceFormat({
                  value: Math.round(project.pastRevenue),
                })
              ) : (
                <Typography sx={{ pr: 3 }}>{'—'}</Typography>
              )}
            </Box>
            <Box sx={{ px: 1, color: COLORS.GRAY.SLATE, minWidth: 0 }}>
              {project.revenueLastInvoiceDate
                ? DateTime.fromFormat(
                    project.revenueLastInvoiceDate,
                    'yyyy-LL-dd',
                  ).toFormat('LL/dd/yyyy')
                : '—'}
            </Box>
            <Box sx={{ px: 1, color: COLORS.GRAY.SLATE, minWidth: 0 }}>
              {getBillingType(project.billingType) || '—'}
            </Box>
            <Box sx={{ px: 1, color: COLORS.GRAY.SLATE, minWidth: 0 }}>
              {project.projectedRevenueEndDate
                ? DateTime.fromFormat(
                    project.projectedRevenueEndDate,
                    'yyyy-LL-dd',
                  ).toFormat('LL/dd/yyyy')
                : '—'}
            </Box>
            <Box sx={{ pl: 2, color: COLORS.GRAY.SLATE, minWidth: 0 }}>
              {project.sowStatus || '—'}
            </Box>
          </Box>
        )}
      >
        <SowResourcesTable
          resources={project.members}
          projectId={project.id}
          onSetEditResourceId={onSetEditResourceId}
          onDeactivateResource={onDeactivateResource}
        />
      </ListItemCard>
    </Box>
  );
};

interface SowResourcesTableProps {
  resources: UserForAdminDto[];
  projectId: string;
  onSetEditResourceId: (resourceId: string) => void;
  onDeactivateResource: (body: DeactivateResourceDto) => void;
}

const SowResourcesTable = ({
  resources,
  projectId,
  onSetEditResourceId,
  onDeactivateResource,
}: SowResourcesTableProps) => {
  const [selectedResourceIds, setSelectedResourceIds] = useState<string[]>([]);
  const [isDeactivateMultipleModalOpen, setIsDeactivateMultipleModalOpen] =
    useState(false);

  const handleToggleAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedResourceIds(resources.map((r) => r.resourceId));
    } else {
      setSelectedResourceIds([]);
    }
  };

  const handleToggleResource = (resourceId: string) => {
    setSelectedResourceIds((prev) =>
      prev.includes(resourceId)
        ? prev.filter((id) => id !== resourceId)
        : [...prev, resourceId],
    );
  };

  return (
    <Box
      sx={{
        '& .MuiTableCell-root': {
          fontSize: '14px',
          display: 'grid',
          columns: 11,
        },
        width: '100%',
        px: 5,
        mt: 2,
        mb: 1,
      }}
      className="no-height"
      key={`${projectId}-resources`}
    >
      <Box
        sx={{
          px: 0,
          py: 0,
          marginLeft: '30px',
          marginRight: '16px',
          mb: 0, // Remove bottom margin
          border: '1px solid #E0E0E0',
          borderRadius: '6px',
          overflow: 'hidden',
          columnSpan: 'all',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.12)',
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '0.4fr 2fr 1fr 1fr 1fr 1fr 1fr 1fr 2fr',
            borderBottom: '1px solid #E0E0E0',
            backgroundColor: (theme) => theme.palette.background.paper,
            '& > .MuiBox-root': {
              padding: '10px 8px',
              color: COLORS.GRAY.SLATE,
              fontSize: '14px',
              columnWidth: 'auto',
              columns: 'all',
              display: 'flex',
              alignItems: 'center',
            },
          }}
        >
          <Box sx={{ columnSpan: 1 }}>
            <Checkbox
              size="small"
              checked={
                resources.length > 0 &&
                selectedResourceIds.length === resources.length
              }
              indeterminate={
                selectedResourceIds.length > 0 &&
                selectedResourceIds.length < resources.length
              }
              onChange={handleToggleAll}
            />
          </Box>
          <Box sx={{ columnSpan: 2 }}>Name</Box>
          <Box sx={{ columnSpan: 1 }}>Seniority</Box>
          <Box sx={{ columnSpan: 1 }}>Position</Box>
          <Box sx={{ columnSpan: 1 }}>Bill rate</Box>
          <Box sx={{ columnSpan: 1 }}>Allocation</Box>
          <Box sx={{ columnSpan: 1 }}>Start date</Box>
          <Box sx={{ columnSpan: 1 }}>End date</Box>
          <Box sx={{ columnSpan: 2 }}>Status</Box>
        </Box>

        {resources.map((resource) => (
          <ResourceRow
            resource={resource}
            onSetEditResourceId={onSetEditResourceId}
            onDeactivateResource={onDeactivateResource}
            isSelected={selectedResourceIds.includes(resource.resourceId)}
            onToggle={() => handleToggleResource(resource.resourceId)}
            key={resource.id}
          />
        ))}
        {selectedResourceIds.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              padding: 2,
              backgroundColor: (theme) => theme.palette.background.paper,
            }}
          >
            <Button
              variant="contained"
              color="error"
              onClick={() => setIsDeactivateMultipleModalOpen(true)}
            >
              Deactivate selected {selectedResourceIds.length}
            </Button>
          </Box>
        )}
      </Box>
      {isDeactivateMultipleModalOpen && (
        <SowDeactivateMultipleModal
          isOpen={isDeactivateMultipleModalOpen}
          onClose={() => setIsDeactivateMultipleModalOpen(false)}
          selectedResourceIds={selectedResourceIds}
          resources={resources.filter((r) =>
            selectedResourceIds.includes(r.resourceId),
          )}
          onClearSelection={() => setSelectedResourceIds([])}
        />
      )}
    </Box>
  );
};

interface ResourceRowProps {
  resource: UserForAdminDto;
  onSetEditResourceId: (resourceId: string) => void;
  onDeactivateResource: (body: DeactivateResourceDto) => void;
  isSelected: boolean;
  onToggle: () => void;
}

const ResourceRow = ({
  resource,
  onSetEditResourceId,
  isSelected,
  onToggle,
}: ResourceRowProps) => {
  const requestDeactivateState = useAppSelector(
    sowAdminSelectors.getRequestDeactivateState,
  );

  const [pendingDeactivation, setPendingDeactivation] = useState(
    requestDeactivateState === DataState.Pending,
  );

  useEffect(() => {
    if (
      [DataState.Fulfilled, DataState.Rejected].includes(requestDeactivateState)
    ) {
      setPendingDeactivation(false);
    }
  }, [requestDeactivateState]);

  const [showEndDateCalendar, setShowEndDateCalendar] = useState(false);
  const deactivateCalendarAnchor = React.useRef<SVGSVGElement | null>(null);

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '0.4fr 2fr 1fr 1fr 1fr 1fr 1fr 1fr 2fr',
        backgroundColor: (theme) => theme.palette.background.paper,
        borderBottom: '1px solid #E0E0E0',
        '&:last-child': {
          borderBottom: 'none',
        },
        '& > .MuiBox-root': {
          display: 'grid',
          flexDirection: 'row',
          padding: '10px 8px',
          fontSize: '14px',
          alignItems: 'center',
        },
      }}
    >
      <Box sx={{ columnSpan: 1 }}>
        <Checkbox size="small" checked={isSelected} onChange={onToggle} />
      </Box>
      <Box sx={{ columnSpan: 2 }}>
        <MemberInfoCell
          length="full"
          userInfo={{
            fullName: resource.fullName,
            avatarUrl: resource.avatarUrl ?? undefined,
          }}
          avatarSmall
        />
      </Box>
      <Box
        sx={{
          columnSpan: 1,
          textWrap: 'nowrap',
          textAlign: 'left',
        }}
      >
        {resource.seniority}
      </Box>
      <Box sx={{ columnSpan: 1 }}>{resource.designation}</Box>
      <Box
        sx={{
          color: !resource.billRate ? COLORS.GRAY.SLATE : 'inherit',
          display: 'flex',
          justifyContent: 'flex-start',
          columnSpan: 1,
        }}
      >
        {resource.billRate ? (
          priceFormat({ value: resource.billRate })
        ) : (
          <Typography>{'—'}</Typography>
        )}
      </Box>
      <Box sx={{ textAlign: 'left', columnSpan: 1 }}>
        {resource.allocation ? `${resource.allocation} %` : `NA`}
      </Box>
      <Box sx={{ columnSpan: 1 }}>
        {resource.startedAt
          ? DateTime.fromFormat(resource.startedAt, 'yyyy-LL-dd').toFormat(
              'LL/dd/yyyy',
            )
          : '—'}
      </Box>
      <Box
        sx={{
          columnSpan: 1,
          color:
            resource.endedAt &&
            DateTime.fromFormat(resource.endedAt, 'yyyy-LL-dd') <
              DateTime.now().startOf('day')
              ? COLORS.GRAY.SLATE
              : 'inherit',
        }}
      >
        {resource.endedAt
          ? DateTime.fromFormat(resource.endedAt, 'yyyy-LL-dd').toFormat(
              'LL/dd/yyyy',
            )
          : '—'}
      </Box>
      <Box
        sx={{
          columnSpan: 2,
        }}
      >
        <Stack
          direction={'row'}
          justifyContent={'space-between'}
          sx={{ pr: 2, verticalAlign: 'center' }}
        >
          <Box>
            {resource.isDeleteReqPending ? (
              <Typography color={(theme: Theme) => theme.palette.warning.main}>
                {'Pending deactivation'}
              </Typography>
            ) : resource.isUpdateReqPending ? (
              <Typography color={(theme: Theme) => theme.palette.warning.main}>
                {'Pending update'}
              </Typography>
            ) : resource.status === ResourceStatus.ACTIVE ? (
              'Active'
            ) : (
              'Inactive'
            )}
          </Box>
          <Box>
            <Tooltip title={'Edit resource'}>
              <EditIcon
                sx={{
                  cursor:
                    resource.status === ResourceStatus.INACTIVE
                      ? 'not-allowed'
                      : 'pointer',
                  opacity:
                    resource.status === ResourceStatus.INACTIVE ? 0.4 : 1,
                  transition: 'background 0.25s',
                  '&:hover': {
                    background: (theme: Theme) =>
                      resource.status === ResourceStatus.INACTIVE
                        ? undefined
                        : theme.palette.action.hover,
                  },
                  borderRadius: 2,
                  color: '#4890fd',
                  mr: 2,
                }}
                onClick={() => {
                  if (resource.status !== ResourceStatus.INACTIVE) {
                    onSetEditResourceId(resource.resourceId);
                  }
                }}
                ref={deactivateCalendarAnchor}
              />
            </Tooltip>
            {pendingDeactivation ? (
              <CircularProgress size={20} color="inherit" />
            ) : resource.isDeleteReqPending ? (
              <DeletionPendingIcon
                sx={{
                  transition: 'background 0.25s',
                  '&:hover': {
                    background: (theme: Theme) => theme.palette.action.hover,
                  },
                  borderRadius: 2,
                  color: (theme: Theme) => theme.palette.warning.main,
                }}
              />
            ) : (
              <>
                <Tooltip title={'Deactivate resource'}>
                  <DeleteOutlined
                    sx={{
                      cursor: 'pointer',
                      transition: 'background 0.25s',
                      '&:hover': {
                        background: (theme: Theme) =>
                          theme.palette.action.hover,
                      },
                      borderRadius: 2,
                      color: '#4890fd',
                    }}
                    onClick={() => {
                      setShowEndDateCalendar(true);
                    }}
                    // ref={deactivateCalendarAnchor}
                  />
                </Tooltip>
              </>
            )}
            {showEndDateCalendar && (
              <SowDeactivateMultipleModal
                isOpen={showEndDateCalendar}
                onClose={() => setShowEndDateCalendar(false)}
                selectedResourceIds={[resource.resourceId]}
                resources={[resource]}
                onClearSelection={() => undefined}
              />
            )}
          </Box>
        </Stack>
      </Box>
    </Box>
  );
};

interface SowResourceEditModalProps {
  resource: UserForAdminDto;
  setEditResourceId: (resourceId: string | null) => void;
  relatedProjects: ProjectAdminDto[];
  relatedProject: ProjectAdminDto;
  relatedAccount: AccountAdminDto;
}

const SowResourceEditModal = ({
  resource,
  relatedProjects,
  relatedProject,
  relatedAccount,
  setEditResourceId,
}: SowResourceEditModalProps) => {
  const dispatch = useAppDispatch();
  const requestMoveState = useAppSelector(
    sowAdminSelectors.getRequestMoveState,
  );
  const requestEditState = useAppSelector(
    sowAdminSelectors.getRequestEditState,
  );
  const [isAcknowledgeNoInvoicesTouched, setIsAcknowledgeNoInvoicesTouched] =
    useState<boolean>(false);

  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);

  const formik = useFormik<
    Pick<
      PutResourceDto,
      | 'newProjectId'
      | 'notes'
      | 'newStartedAt'
      | 'newEndedAt'
      | 'position'
      | 'seniority'
      | 'billRate'
      | 'allocation'
    > & {
      acknowledgeNoInvoices?: boolean;
      oldEndedAt?: string;
      oldAllocation?: number;
      moveAction?: 'deactivate' | 'modify' | 'nothing';
      seniority?: Seniority;
    }
  >({
    initialValues: {
      newProjectId: relatedProject.id,
      notes: null,
      newStartedAt: resource.startedAt
        ? DateTime.fromFormat(resource.startedAt, 'yyyy-MM-dd', {
            zone: 'utc',
          }).toFormat('yyyy-MM-dd')
        : DateTime.now().setZone('utc').toFormat('yyyy-MM-dd'),
      newEndedAt: resource.endedAt
        ? resource.endedAt
        : DateTime.now().setZone('utc').endOf('year').toFormat('yyyy-MM-dd'),
      position:
        getSowStreamFromLabel(resource.designation) ?? SowStream.DEVELOPER,
      seniority: resource.seniority,
      billRate: resource.billRate,
      allocation: resource.allocation,

      oldEndedAt: resource.endedAt
        ? resource.endedAt
        : DateTime.now().setZone('utc').endOf('year').toFormat('yyyy-MM-dd'),
      oldAllocation: resource.allocation,

      acknowledgeNoInvoices: false,
      moveAction: undefined,
    },
    enableReinitialize: true,
    validate: (values) => {
      const errors: Record<string, string> = {};

      // Required fields
      if (!values.newProjectId) {
        errors.newProjectId = 'Project ID is required';
      }

      if (!values.newStartedAt) {
        errors.newStartedAt = 'Start date is required';
      }

      if (!values.newEndedAt) {
        errors.newEndedAt = 'End date is required';
      }

      // Field validations
      if (values.notes && values.notes.length > 250) {
        errors.notes = 'Notes cannot exceed 250 characters';
      }

      if (!values.position) {
        errors.position = 'Position is required';
      }

      if (values.position && values.position.length > 30) {
        errors.position = 'Position cannot exceed 30 characters';
      }

      if (!values.seniority) {
        errors.seniority = 'Seniority is required';
      }

      if (
        values.seniority &&
        !Object.values(Seniority).includes(values.seniority as Seniority)
      ) {
        errors.seniority = 'Invalid seniority level';
      }

      if (values.billRate !== undefined && (values.billRate as any) !== '') {
        if (Math.floor(Number(values.billRate)) !== Number(values.billRate)) {
          errors.billRate = 'Bill rate must be a whole number';
        } else if (values.billRate < 0 || values.billRate > 1000) {
          errors.billRate = 'Bill rate must be between 0 and 1000';
        }
      } else {
        errors.billRate = 'Bill rate is required';
      }

      if (
        values.allocation !== undefined &&
        (values.allocation as any) !== ''
      ) {
        if (
          Math.floor(Number(values.allocation)) !== Number(values.allocation)
        ) {
          errors.allocation = 'Allocation must be a whole number';
        } else if (values.allocation < 0 || values.allocation > 100) {
          errors.allocation = 'Allocation must be between 0 and 100';
        }
      } else {
        errors.allocation = 'Allocation is required';
      }

      // Cross field validations
      if (values.newStartedAt && values.newEndedAt) {
        if (new Date(values.newEndedAt) <= new Date(values.newStartedAt)) {
          errors.newEndedAt = 'End date must be after start date';
        }
      }

      if (values.moveAction === 'modify') {
        if (!values.oldEndedAt) {
          errors.oldEndedAt = 'End date of current resource is required';
        }
        if (!values.oldAllocation) {
          errors.oldAllocation = 'Allocation of current resource is required';
        } else if (isNaN(Number(values.oldAllocation))) {
          errors.oldAllocation = 'Allocation must be a number';
        } else if (
          Number(values.oldAllocation) < 0 ||
          Number(values.oldAllocation) > 100
        ) {
          errors.oldAllocation = 'Allocation must be between 0 and 100';
        }

        if (values.oldEndedAt) {
          const oldEndDate = DateTime.fromISO(values.oldEndedAt);
          const now = DateTime.now();
          const startOfCurrentMonth = now.startOf('month');

          if (oldEndDate < startOfCurrentMonth) {
            errors.oldEndedAt =
              'End date of current resource must be in current month or future. Otherwise, confirm with finance team why you need to change the end date to before the previous billing period';
          }

          if (!resource.startedAt) {
            errors.oldEndedAt =
              'Set proper Start Date & End Date for the resource in Edit, then Move it';
          } else {
            if (
              oldEndDate < DateTime.fromFormat(resource.startedAt, 'yyyy-LL-dd')
            ) {
              errors.oldEndedAt =
                'End date of current resource must be after the start date';
            }
          }
        }
      }

      if (values.moveAction === 'deactivate' && !values.acknowledgeNoInvoices) {
        errors.acknowledgeNoInvoices =
          'You must acknowledge that there will be no new invoices generated for this resource';
      }

      return errors;
    },
    onSubmit: (values) => {
      if (values.newProjectId !== relatedProject.id) {
        dispatch(
          requestMove({
            newProjectId: values.newProjectId,
            oldProjectId: relatedProject.id,
            newEndedAt: values.newEndedAt,
            newStartedAt: values.newStartedAt,
            resourceId: resource.resourceId,
            notes: values.notes,
            allocation: Number(values.allocation),
            billRate: Number(values.billRate),
            seniority: values.seniority,
            position: values.position,

            prevResourceAction: (() => {
              if (
                formik.values.moveAction === 'deactivate' ||
                formik.values.moveAction === 'nothing'
              ) {
                return {
                  action: formik.values.moveAction,
                };
              } else if (formik.values.moveAction === 'modify') {
                return {
                  action: 'modify',
                  oldEndedAt: values.oldEndedAt,
                  oldAllocation: Number(values.oldAllocation),
                };
              }

              throw new Error('Invalid move action');
            })(),

            files: selectedFiles,
          }),
        );
      } else {
        dispatch(
          requestEdit({
            resourceId: resource.resourceId,
            allocation: Number(values.allocation),
            billRate: Number(values.billRate),
            notes: values.notes ?? undefined,
            seniority: values.seniority,
            position: values.position,
            startedAt: values.newStartedAt,
            endedAt: values.newEndedAt ?? undefined,
            files: selectedFiles,
          }),
        );
      }
    },
  });

  const { seniority, position, setFieldValue } = useMemo(() => {
    return {
      seniority: formik.values.seniority,
      position: formik.values.position,
      setFieldValue: formik.setFieldValue,
    };
  }, [formik.values.seniority, formik.values.position, formik.setFieldValue]);

  const seniorities = useMemo(() => {
    if (!sowStreamValues.includes(position)) {
      return seniorityValues;
    }

    const newAvailableSeniorities = getSowSeniorities(position);

    // If new stream doesnt support current seniority, reset it
    if (!newAvailableSeniorities.includes(seniority)) {
      setFieldValue('seniority', undefined);
    }

    return newAvailableSeniorities;
  }, [seniority, position, setFieldValue]);

  const handleSelectedOption = useCallback(
    (event: SelectChangeEvent) => {
      if (!event.target.value) return;

      const project = relatedProjects.find(
        (project) => project.id === event.target.value,
      );
      if (!project) return;
      if (project.isFoundInZp) {
        formik.setFieldValue('newProjectId', event.target.value);
      }
    },
    [formik, relatedProjects],
  );

  const handleNotesChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      formik.setFieldValue('notes', event.target.value);
    },
    [formik],
  );

  const handleStreamChange = useCallback(
    (event: SelectChangeEvent<SowStream>) => {
      formik.setFieldValue('position', event.target.value);
    },
    [formik],
  );

  const handleAllocationChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;

      if (!/^\d{0,3}$/.test(value)) {
        return;
      }

      formik.setFieldValue('allocation', value);
    },
    [formik],
  );

  const handleBillRateChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;

      if (!/^\d{0,4}$/.test(value)) {
        return;
      }

      formik.setFieldValue('billRate', value);
    },
    [formik],
  );

  const handleSeniorityChange = useCallback(
    (event: SelectChangeEvent<Seniority>) => {
      formik.setFieldValue('seniority', event.target.value);
    },
    [formik],
  );

  const handleCloseModal = useCallback(() => {
    setEditResourceId(null);
    dispatch(resetRequestStates());
  }, [setEditResourceId, dispatch]);

  const handleAcknowledgeNoInvoicesChange = useCallback(() => {
    formik.setFieldValue(
      'acknowledgeNoInvoices',
      !formik.values.acknowledgeNoInvoices,
    );
    setIsAcknowledgeNoInvoicesTouched(true);
  }, [formik]);

  const handleFileChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      if (!files) return;

      setSelectedFiles((prev) => [...prev, ...Array.from(files)]);
    },
    [],
  );

  const handleRemoveFile = useCallback((fileName: string) => {
    setSelectedFiles((prev) => prev.filter((file) => file.name !== fileName));
  }, []);

  useEffect(() => {
    if (
      requestMoveState === DataState.Fulfilled ||
      requestEditState === DataState.Fulfilled
    ) {
      handleCloseModal();
    }
  }, [requestMoveState, requestEditState, handleCloseModal]);

  return (
    <Dialog
      open={!!resource.id}
      onClose={handleCloseModal}
      maxWidth="sm"
      scroll="body"
      fullWidth
    >
      <DialogTitle>
        {formik.values.newProjectId === relatedProject.id
          ? `Edit resource`
          : `Move resource`}
        <IconButton
          aria-label="close"
          onClick={handleCloseModal}
          sx={{
            position: 'absolute',
            right: 14,
            top: 14,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ my: 1, display: 'grid', gap: 2 }}>
          <MemberInfoCell
            userInfo={{
              avatarUrl: resource.avatarUrl ?? undefined,
              fullName: resource.fullName,
              stream: `${resource.seniority} ${resource.designation}`,
            }}
          />
        </Box>
        <Box sx={{ mt: 3, display: 'grid', gap: 2 }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              name="accountId"
              label="Account"
              disabled
              value={relatedAccount.name}
              defaultValue={relatedAccount.name}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>Project</InputLabel>
              <Select
                name="newProjectId"
                label="Project"
                disabled={!relatedProject.isFoundInZp}
                onChange={handleSelectedOption}
                error={Boolean(formik.errors.newProjectId)}
                value={formik.values.newProjectId}
              >
                {relatedProjects.map((project) => {
                  return (
                    <MenuItem
                      key={project.id}
                      value={project.id}
                      disabled={project.id === relatedProject.id}
                      sx={{
                        ...(!project.isFoundInZp && {
                          color: COLORS.GRAY.SLATE,
                          cursor: 'not-allowed',
                          backgroundColor: COLORS.HIGHLIGHTS.NEUTRAL,
                        }),
                      }}
                    >
                      <Tooltip
                        title={
                          !project.isFoundInZp
                            ? 'This project is not found in ZCRM, please fix the naming or add the project to ZCRM'
                            : ''
                        }
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            width: '100%',
                          }}
                        >
                          {project.name}
                          {!project.isFoundInZp && (
                            <CircleIcon
                              sx={{ color: '#EF5350', fontSize: 12, ml: 1 }}
                            />
                          )}
                        </Box>
                      </Tooltip>
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Position</InputLabel>
              <Select
                name="position"
                label="Position"
                onChange={handleStreamChange}
                error={Boolean(formik.errors.position)}
                value={formik.values.position}
              >
                {sowStreamValues.map((stream) => {
                  return (
                    <MenuItem key={stream} value={stream}>
                      {sowStreamsLabels[stream]}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Seniority</InputLabel>
              <Select
                name="seniority"
                label="Seniority"
                onChange={handleSeniorityChange}
                error={Boolean(formik.errors.seniority)}
                value={formik.values.seniority}
              >
                {seniorities.map((seniority) => {
                  return (
                    <MenuItem key={seniority} value={seniority}>
                      {seniority}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <DatePicker
              onChange={(date) =>
                formik.setFieldValue(
                  'newStartedAt',
                  date ? formatDateTo_yyyy_MM_dd(date) : null,
                )
              }
              value={formatStringToDate(formik.values.newStartedAt)}
              format="MM/dd/yyyy"
              label="Start date"
              slotProps={{
                textField: {
                  error: Boolean(formik.errors.newStartedAt),
                  id: 'newStartedAt',
                  name: 'newStartedAt',
                },
              }}
            />
            <DatePicker
              onChange={(date) =>
                formik.setFieldValue(
                  'newEndedAt',
                  date ? formatDateTo_yyyy_MM_dd(date) : null,
                )
              }
              value={
                formik.values.newEndedAt
                  ? formatStringToDate(formik.values.newEndedAt)
                  : null
              }
              format="MM/dd/yyyy"
              label="End date"
              slotProps={{
                textField: {
                  error: Boolean(formik.errors.newEndedAt),
                  id: 'newEndedAt',
                  name: 'newEndedAt',
                },
              }}
            />
          </Box>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              fullWidth
              id="allocation"
              type="number"
              sx={{
                '& .MuiInputBase-input': {
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                },
              }}
              label="Allocation"
              placeholder="Add allocation"
              value={formik.values.allocation}
              name={`allocation`}
              error={Boolean(formik.errors.allocation)}
              helperText={formik.errors.allocation}
              onChange={handleAllocationChange}
              inputProps={{
                max: 100,
                min: 0,
              }}
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              }}
            />

            <TextField
              fullWidth
              id="billRate"
              type="number"
              sx={{
                '& .MuiInputBase-input': {
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                },
              }}
              label="Bill Rate"
              placeholder="Add bill rate"
              value={formik.values.billRate}
              name={`billRate`}
              error={Boolean(formik.errors.billRate)}
              helperText={formik.errors.billRate}
              onChange={handleBillRateChange}
              inputProps={{
                min: 0,
                max: 1000,
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">$</InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">/h</InputAdornment>
                ),
              }}
            />
          </Box>

          <TextField
            id="notes"
            type="text"
            fullWidth
            multiline
            minRows={2}
            sx={{ width: '100%' }}
            label="Notes"
            placeholder="Add notes"
            value={formik.values.notes ?? ''}
            name={`notes`}
            error={Boolean(formik.errors.notes)}
            onChange={handleNotesChange}
            helperText={formik.errors.notes}
          />

          <Divider />
          {formik.values.newProjectId !== relatedProject.id && (
            <FormControl fullWidth>
              <InputLabel>Action for Current Resource</InputLabel>
              <Select
                value={formik.values.moveAction || ''}
                onChange={(e) =>
                  formik.setFieldValue(
                    'moveAction',
                    e.target.value as 'deactivate' | 'modify' | 'nothing',
                  )
                }
                label="Action for current resource"
              >
                <MenuItem value="deactivate">
                  Deactivate Current Resource
                </MenuItem>
                <MenuItem value="modify">Change End Date & Allocation</MenuItem>
                <MenuItem value="nothing">
                  Leave Current Resource As Is
                </MenuItem>
              </Select>
            </FormControl>
          )}

          {formik.values.moveAction === 'modify' && (
            <Box sx={{ display: 'flex', gap: 2 }}>
              <DatePicker
                label="Old End Date"
                value={
                  formik.values.oldEndedAt
                    ? formatStringToDate(formik.values.oldEndedAt)
                    : null
                }
                format="MM/dd/yyyy"
                onChange={(date) =>
                  formik.setFieldValue(
                    'oldEndedAt',
                    date ? formatDateTo_yyyy_MM_dd(date) : null,
                  )
                }
                slotProps={{
                  textField: {
                    error: Boolean(formik.errors.oldEndedAt),
                    helperText: formik.errors.oldEndedAt,
                  },
                }}
              />
              <TextField
                fullWidth
                label="Old Allocation"
                value={formik.values.oldAllocation}
                error={Boolean(formik.errors.oldAllocation)}
                helperText={formik.errors.oldAllocation}
                onChange={(e) =>
                  formik.setFieldValue('oldAllocation', e.target.value)
                }
              />
            </Box>
          )}
          {formik.values.moveAction === 'deactivate' && (
            <Box sx={{ display: 'grid', gap: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={Boolean(formik.values.acknowledgeNoInvoices)}
                    onChange={handleAcknowledgeNoInvoicesChange}
                  />
                }
                label="I acknowledge that there will be no new invoices generated for this resource"
              />
            </Box>
          )}

          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 2,
            }}
          >
            <Box>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />
              <Button
                onClick={() => fileInputRef.current?.click()}
                size="small"
                startIcon={<AttachFileIcon />}
              >
                Add files
              </Button>
            </Box>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 0.5,
            }}
          >
            {selectedFiles.map((file) => (
              <Chip
                key={file.name}
                label={file.name}
                size="small"
                onDelete={() => handleRemoveFile(file.name)}
                sx={{ maxWidth: '100%' }}
              />
            ))}
          </Box>

          <Box
            alignItems={'center'}
            display={'flex'}
            gap={1.5}
            sx={{
              backgroundColor: COLORS.HIGHLIGHTS.NEUTRAL,
              padding: 1.5,
              borderRadius: 2,
            }}
          >
            <InfoOutlined color="secondary" />
            <Typography variant="body2" color="text.secondary">
              An email notification will be sent to Finance, Zoho People team
              and the Project&apos;s Delivery Manager for review and processing.
            </Typography>
          </Box>

          {parseFormikErrors(
            formik.errors.newStartedAt || formik.errors.newEndedAt,
          ).map((error) => (
            <Typography variant="body2" color="error" key={error}>
              {error}
            </Typography>
          ))}

          {isAcknowledgeNoInvoicesTouched &&
            parseFormikErrors(formik.errors.acknowledgeNoInvoices).map(
              (error) => (
                <Typography variant="body2" color="error" key={error}>
                  {error}
                </Typography>
              ),
            )}

          <Button
            variant="contained"
            onClick={() => formik.handleSubmit()}
            disabled={
              requestMoveState === DataState.Pending ||
              requestEditState === DataState.Pending
            }
          >
            {requestMoveState === DataState.Pending ||
            requestEditState === DataState.Pending ? (
              <CircularProgress size={24} color="inherit" />
            ) : formik.values.newProjectId === relatedProject.id ? (
              'Edit Resource'
            ) : (
              'Swap Project'
            )}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

interface SowChReqPopoverProps {
  sowChangeReqsPopoverAnchor: React.MutableRefObject<HTMLElement | null>;
  sowSpreadsheetLink: string;
  sowChangeReqs: SowChangeReqForApprovalDto[];
  isOpen: boolean;
}

const SowChReqPopover = ({
  sowChangeReqsPopoverAnchor,
  sowSpreadsheetLink,
  sowChangeReqs,
  isOpen,
}: SowChReqPopoverProps) => {
  const dispatch = useAppDispatch();

  const executeSowChangeReqState = useAppSelector(
    sowAdminSelectors.getExecuteSowChangeReqState,
  );
  const rejectSowChangeReqState = useAppSelector(
    sowAdminSelectors.getRejectSowChangeReqState,
  );
  const sowChangeReqStatuses = useAppSelector(
    sowAdminSelectors.getSowChangeReqStatuses,
  );

  const handleApprove = useCallback(
    (id: string) => {
      dispatch(executeSowChangeReq({ id }));
    },
    [dispatch],
  );

  const handleReject = useCallback(
    (id: string) => {
      dispatch(rejectSowChangeReq({ id }));
    },
    [dispatch],
  );

  return (
    <Box component="span">
      <Popover
        style={{ pointerEvents: 'none' }}
        open={isOpen}
        anchorEl={sowChangeReqsPopoverAnchor.current}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            marginTop: '8px',
            pointerEvents: 'auto',
            width: '500px',
            gap: '1.5rem',
            maxHeight: '400px',
          },
        }}
      >
        <Box sx={{ padding: '24px 16px' }}>
          <Typography variant="h3" sx={{ marginBottom: '16px' }}>
            Change requests
          </Typography>

          {sowChangeReqs.map(
            (sowChangeReq: SowChangeReqForApprovalDto, index: number) => (
              <SowChangeReqItem
                key={sowChangeReq.id}
                sowChangeReq={sowChangeReq}
                index={index}
                sowSpreadsheetLink={sowSpreadsheetLink}
                executeSowChangeReqState={executeSowChangeReqState}
                rejectSowChangeReqState={rejectSowChangeReqState}
                sowChangeReqStatuses={sowChangeReqStatuses[sowChangeReq.id]}
                handleApprove={handleApprove}
                handleReject={handleReject}
                isLastItem={index === sowChangeReqs.length - 1}
              />
            ),
          )}
        </Box>
      </Popover>
    </Box>
  );
};

interface SowChangeReqItemProps {
  sowChangeReq: SowChangeReqForApprovalDto;
  index: number;
  sowSpreadsheetLink: string;
  executeSowChangeReqState: DataState;
  rejectSowChangeReqState: DataState;
  sowChangeReqStatuses:
    | { status: boolean; emailSentStatus?: boolean; sowAppendStatus?: boolean }
    | undefined;
  handleApprove: (id: string) => void;
  handleReject: (id: string) => void;
  isLastItem: boolean;
}

const SowChangeReqItem = ({
  sowChangeReq,
  index,
  sowSpreadsheetLink,
  executeSowChangeReqState,
  rejectSowChangeReqState,
  sowChangeReqStatuses,
  isLastItem,

  handleApprove,
  handleReject,
}: SowChangeReqItemProps) => {
  return (
    <Box
      key={index}
      sx={{
        textAlign: 'left',
        backgroundColor: COLORS.HIGHLIGHTS.NEUTRAL,
        padding: '16px',
        ...(isLastItem && {
          marginBottom: '8px',
        }),
        borderRadius: '8px',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          paddingBottom: '8px',
        }}
      >
        <MemberInfoCell
          userInfo={{
            fullName: sowChangeReq.raisedBy.fullName,
            avatarUrl: sowChangeReq.raisedBy.avatarUrl ?? undefined,
            stream: sowChangeReq.raisedBy.stream ?? undefined,
          }}
        />
        <Typography variant="body2" sx={{ ml: 'auto' }}>
          {DateTime.fromISO(sowChangeReq.createdAt).toFormat('LL/dd/yyyy')}
        </Typography>
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Box sx={{ mb: 1 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {sowChangeReq.adminNotes}
          </Typography>
          {sowChangeReq.userNotes && (
            <Typography variant="body2" sx={{ mb: 1 }}>
              From requestor &quot;{sowChangeReq.userNotes}&quot;
            </Typography>
          )}
          {sowChangeReq.action === BusinessAction.MOVE_RESOURCE && (
            <Typography variant="body2">
              A new record will be appended to the SOW spreadsheet. Please, mark
              the user&apos;s previous one as inactive in the{' '}
              <a
                href={sowSpreadsheetLink}
                target="_blank"
                rel="noopener noreferrer"
              >
                SOW spreadsheet
              </a>
              .
            </Typography>
          )}
        </Box>
        {/* If no result is sent from the backend, show the buttons */}
        {sowChangeReqStatuses === undefined && (
          <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
            <IconButton
              size="small"
              onClick={() => handleApprove(sowChangeReq.id)}
              disabled={
                executeSowChangeReqState === DataState.Pending ||
                rejectSowChangeReqState === DataState.Pending
              }
            >
              {executeSowChangeReqState === DataState.Pending ? (
                <CircularProgress size={20} color="success" />
              ) : (
                <CheckIcon sx={{ color: 'success.main' }} />
              )}
            </IconButton>
            <IconButton
              size="small"
              onClick={() => handleReject(sowChangeReq.id)}
              disabled={
                executeSowChangeReqState === DataState.Pending ||
                rejectSowChangeReqState === DataState.Pending
              }
            >
              {rejectSowChangeReqState === DataState.Pending ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <CloseIcon sx={{ color: 'text.secondary' }} />
              )}
            </IconButton>
          </Box>
        )}
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Box sx={{ mb: 1 }}>
          {sowChangeReqStatuses?.emailSentStatus !== undefined &&
            sowChangeReqStatuses.emailSentStatus === false && (
              <Alert severity="error" sx={{ mb: 1 }}>
                Email not sent, please send the email manually, and report the
                issue to the dev team.
              </Alert>
            )}
          {sowChangeReqStatuses?.sowAppendStatus !== undefined &&
            sowChangeReqStatuses.sowAppendStatus === false && (
              <Alert severity="error">
                SOW record not appended, please append a new SOW record
                manually, and report the issue to the dev team.
              </Alert>
            )}
        </Box>
      </Box>
    </Box>
  );
};

interface SowDeactivateMultipleModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedResourceIds: string[];
  resources?: UserForAdminDto[];
  onClearSelection: () => void;
}

const SowDeactivateMultipleModal = ({
  isOpen,
  onClose,
  selectedResourceIds,
  resources,
  onClearSelection,
}: SowDeactivateMultipleModalProps) => {
  const dispatch = useAppDispatch();
  const requestDeactivateState = useAppSelector(
    sowAdminSelectors.getRequestDeactivateState,
  );
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);

  const resource = resources?.length === 1 ? resources[0] : null;

  const startDateText = useMemo(() => {
    if (!resources || resources.length === 0) return '';
    const firstStart = resources[0].startedAt;
    const allSame = resources.every((r) => r.startedAt === firstStart);
    if (!allSame) return 'Varies';
    return firstStart
      ? DateTime.fromISO(firstStart).toFormat('MM/dd/yyyy')
      : 'N/A';
  }, [resources]);

  const defaultEndedAt = useMemo(() => {
    if (!resources || resources.length === 0) {
      return DateTime.now().setZone('utc').toFormat('yyyy-MM-dd');
    }
    const firstEnd = resources[0].endedAt;
    if (firstEnd) {
      const allSame = resources.every((r) => r.endedAt === firstEnd);
      if (allSame) {
        return DateTime.fromFormat(firstEnd, 'yyyy-MM-dd', {
          zone: 'utc',
        }).toFormat('yyyy-MM-dd');
      }
    }
    return DateTime.now().setZone('utc').toFormat('yyyy-MM-dd');
  }, [resources]);

  const formik = useFormik({
    initialValues: {
      endedAt: defaultEndedAt,
      deactivationReason: DEACTIVATION_REASONS[0] as DeactivationReason,
      notes: '',
    },
    enableReinitialize: true,
    validate: (values) => {
      const errors: Record<string, string> = {};
      if (!values.endedAt) {
        errors.endedAt = 'End date is required';
      }
      if (!values.deactivationReason) {
        errors.deactivationReason = 'Deactivation reason is required';
      }
      if (!values.notes?.trim()) {
        errors.notes = 'Notes are required';
      } else if (values.notes.length > 250) {
        errors.notes = 'Notes cannot exceed 250 characters';
      }
      return errors;
    },
    onSubmit: async (values) => {
      if (selectedResourceIds.length === 0) return;

      try {
        if (selectedResourceIds.length === 1) {
          const payload: DeactivateResourceDto = {
            resourceId: selectedResourceIds[0],
            endedAt: values.endedAt,
            deactivationReason: values.deactivationReason as DeactivationReason,
            notes: values.notes,
            files: selectedFiles,
          };
          await dispatch(requestDeactivate(payload)).unwrap();
        } else {
          const payload: DeactivateMultipleResourceDto = {
            resourceIds: selectedResourceIds,
            endedAt: values.endedAt,
            deactivationReason: values.deactivationReason as DeactivationReason,
            notes: values.notes,
            files: selectedFiles,
          };
          await dispatch(requestBulkDeactivate(payload)).unwrap();
        }

        onClearSelection();
        setSelectedFiles([]);
        formik.resetForm();
        onClose();
      } catch (err) {
        // Error handled globally by the thunk
      }
    },
  });

  const handleFileChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      if (!files) return;
      setSelectedFiles((prev) => [...prev, ...Array.from(files)]);
    },
    [],
  );

  const handleRemoveFile = useCallback((fileName: string) => {
    setSelectedFiles((prev) => prev.filter((file) => file.name !== fileName));
  }, []);

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth={false}
      sx={{
        '& .MuiDialog-paper': {
          width: 632,
        },
      }}
      fullWidth
      scroll="body"
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Typography sx={{ flexGrow: 1, fontSize: '24px', fontWeight: 500 }}>
          Deactivate
        </Typography>

        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Box
        gap={1.5}
        sx={{
          backgroundColor: COLORS.HIGHLIGHTS.NEUTRAL,
          py: 1,
          px: 3,
        }}
      >
        {resource ? (
          <MemberInfoCell
            length="full"
            userInfo={{
              fullName: resource.fullName,
              avatarUrl: resource.avatarUrl ?? undefined,
              stream: `${resource.seniority} ${resource.designation}`,
            }}
          />
        ) : (
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar
              sx={{
                bgcolor: COLORS.BLUE.PRIMARY,
                width: 40,
                height: 40,
                borderRadius: 3,
              }}
            >
              <PeopleOutlineOutlined />
            </Avatar>

            <Typography>
              {selectedResourceIds.length} people selected
            </Typography>
          </Stack>
        )}
      </Box>
      <DialogContent>
        <Box sx={{ my: 1, display: 'grid', gap: 2 }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Box sx={{ flex: 1 }}>
              <TextField
                disabled
                fullWidth
                size="medium"
                label="Start date"
                value={startDateText}
                InputProps={
                  startDateText !== 'Varies'
                    ? {
                        endAdornment: (
                          <InputAdornment position="end">
                            <CalendarIcon fontSize="medium" />
                          </InputAdornment>
                        ),
                      }
                    : undefined
                }
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <DatePicker
                onChange={(date) =>
                  formik.setFieldValue(
                    'endedAt',
                    date ? formatDateTo_yyyy_MM_dd(date) : null,
                  )
                }
                value={
                  formik.values.endedAt
                    ? formatStringToDate(formik.values.endedAt)
                    : null
                }
                format="MM/dd/yyyy"
                label="End date"
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: 'medium',
                    error: Boolean(formik.errors.endedAt),
                    id: 'endedAt',
                    name: 'endedAt',
                    helperText: formik.errors.endedAt,
                  },
                }}
              />
            </Box>
            <Box sx={{ flex: 1.4, display: 'flex' }}>
              <FormControl
                fullWidth
                size="medium"
                error={Boolean(formik.errors.deactivationReason)}
              >
                <InputLabel id="bulk-deactivation-reason-label">
                  Deactivation reason
                </InputLabel>
                <Select
                  labelId="bulk-deactivation-reason-label"
                  id="bulk-deactivation-reason"
                  name="deactivationReason"
                  value={formik.values.deactivationReason}
                  label="Deactivation reason"
                  onChange={formik.handleChange}
                >
                  {DEACTIVATION_REASONS.map((reason) => (
                    <MenuItem key={reason} value={reason}>
                      {reason}
                    </MenuItem>
                  ))}
                </Select>
                {formik.errors.deactivationReason && (
                  <Typography variant="caption" color="error">
                    {formik.errors.deactivationReason}
                  </Typography>
                )}
              </FormControl>
            </Box>
          </Box>
          <TextField
            id="notes"
            type="text"
            fullWidth
            multiline
            minRows={2}
            sx={{ width: '100%' }}
            label="Notes"
            placeholder="Add notes"
            value={formik.values.notes ?? ''}
            name="notes"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={Boolean(formik.touched.notes && formik.errors.notes)}
            helperText={formik.touched.notes && formik.errors.notes}
          />
          <Box>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
            <Button
              onClick={() => fileInputRef.current?.click()}
              size="small"
              startIcon={<AttachFileIcon />}
            >
              Add files
            </Button>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 0.5,
            }}
          >
            {selectedFiles.map((file) => (
              <Chip
                key={file.name}
                label={file.name}
                size="small"
                onDelete={() => handleRemoveFile(file.name)}
                sx={{ maxWidth: 430 }}
              />
            ))}
          </Box>

          <Box
            alignItems="center"
            display="flex"
            gap={1.5}
            sx={{
              backgroundColor: COLORS.HIGHLIGHTS.NEUTRAL,
              padding: 1.5,
              borderRadius: 2,
              mb: 0,
            }}
          >
            <EmailOutlined color="secondary" />
            <Typography variant="body2" color="text.secondary">
              Finance, Zoho People team and the Project&apos;s Delivery Manager
              will be notified to review and process the request.
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Stack
          direction="row"
          spacing={2}
          justifyContent="flex-end"
          width="100%"
        >
          <Button key={'cancel'} onClick={onClose}>
            Cancel
          </Button>

          <Button
            variant="contained"
            color="error"
            onClick={() => formik.handleSubmit()}
            disabled={requestDeactivateState === DataState.Pending}
          >
            {requestDeactivateState === DataState.Pending ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              'Deactivate'
            )}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};
